import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  AlertTriangle, ShieldAlert, FileText, Clock, ChevronRight, X, RefreshCw,
  CheckCircle2, XCircle, Info, ArrowRight, Zap, ThumbsUp, ThumbsDown,
  Globe, Package, RotateCw, Eye
} from 'lucide-react';

const fmt = (c) => { if (c == null) return '$0'; return `$${(Math.abs(c)/100).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`; };

function PredictiveLabel() {
  return <span className="inline-flex items-center gap-1 text-[9px] font-medium text-violet-600 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5"><Zap className="h-2.5 w-2.5" />Predictive Insight</span>;
}

function RiskBadge({ level, label }) {
  const colors = { high: 'bg-red-100 text-red-700 border-red-200', medium: 'bg-amber-100 text-amber-700 border-amber-200', low: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
  return <span className={`inline-flex items-center gap-1 text-[10px] font-medium border rounded px-1.5 py-0.5 ${colors[level] || 'bg-muted text-muted-foreground'}`}><Zap className="h-2.5 w-2.5" />{label || level} risk</span>;
}

// ─── CONFIDENCE COLORED TEXT ───

function ConfidenceText({ level, score }) {
  const colors = { high: 'text-emerald-600', medium: 'text-amber-600', low: 'text-gray-400' };
  return <span className={`font-medium ${colors[level] || 'text-gray-400'}`}>{level}{score ? ` (${score}%)` : ''}</span>;
}

// ─── FEEDBACK BUTTONS (👍/👎) ───

function FeedbackButtons({ predictionId }) {
  const [sent, setSent] = useState(null);
  const send = async (feedback) => {
    try { await api.post('/rcm/predictive/feedback', { prediction_id: predictionId || '', feedback }); setSent(feedback); } catch {}
  };
  if (sent) return <span className="text-[10px] text-muted-foreground">{sent === 'helpful' ? 'Marked helpful' : 'Marked not helpful'}</span>;
  return (
    <span className="inline-flex gap-1" data-testid="feedback-buttons">
      <button onClick={() => send('helpful')} className="text-muted-foreground hover:text-emerald-600 transition-colors" title="Helpful"><ThumbsUp className="h-3 w-3" /></button>
      <button onClick={() => send('not_helpful')} className="text-muted-foreground hover:text-red-600 transition-colors" title="Not helpful"><ThumbsDown className="h-3 w-3" /></button>
    </span>
  );
}

// ─── STATUS CHIP ───

function StatusChip({ status, size = 'sm' }) {
  const colors = {
    pending_review: 'bg-amber-100 text-amber-700', ready_to_submit: 'bg-sky-100 text-sky-700',
    submitted: 'bg-blue-100 text-blue-700', acknowledged: 'bg-indigo-100 text-indigo-700',
    accepted: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700',
    failed_transport: 'bg-red-100 text-red-700', needs_attention: 'bg-orange-100 text-orange-700',
    open: 'bg-amber-100 text-amber-700', under_review: 'bg-sky-100 text-sky-700',
    corrected: 'bg-teal-100 text-teal-700', resubmission_ready: 'bg-indigo-100 text-indigo-700',
    resubmitted: 'bg-emerald-100 text-emerald-700', closed: 'bg-gray-100 text-gray-500',
    corrected_claim_ready: 'bg-teal-100 text-teal-700', appeal_candidate: 'bg-purple-100 text-purple-700',
    appealed: 'bg-purple-100 text-purple-700', rebilled: 'bg-emerald-100 text-emerald-700',
    written_off: 'bg-gray-100 text-gray-500', pending_provider_input: 'bg-orange-100 text-orange-700',
    resolved: 'bg-emerald-100 text-emerald-700',
    ready_for_posting_review: 'bg-sky-100 text-sky-700', posting_exception: 'bg-red-100 text-red-700',
    matched_needs_review: 'bg-amber-100 text-amber-700', held_unmatched: 'bg-red-100 text-red-700',
    approved_for_posting: 'bg-emerald-100 text-emerald-700', posted: 'bg-emerald-100 text-emerald-700',
    matched: 'bg-emerald-100 text-emerald-700', partially_matched: 'bg-amber-100 text-amber-700',
    unmatched: 'bg-red-100 text-red-700',
  };
  const c = colors[status] || 'bg-muted text-muted-foreground';
  const sz = size === 'xs' ? 'text-[10px] px-1.5 py-0' : 'text-xs px-2 py-0.5';
  return <span className={`${c} ${sz} rounded-full font-medium whitespace-nowrap`} data-testid={`status-${status}`}>{(status||'').replace(/_/g,' ')}</span>;
}

// ─── SLIDE PANEL ───

function SlidePanel({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={onClose} data-testid="slide-panel">
      <div className="w-full max-w-lg bg-card border-l shadow-xl overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-card border-b px-4 py-3 flex justify-between items-center z-10">
          <h3 className="text-sm font-semibold">{title}</h3>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-panel"><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

// ─── TIMELINE ───

function Timeline({ events }) {
  if (!events?.length) return <p className="text-xs text-muted-foreground">No history</p>;
  return (
    <div className="space-y-2">{events.map((ev, i) => (
      <div key={i} className="flex gap-2 text-xs">
        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
        <div>
          <span className="font-medium">{(ev.event_type || ev.action || '').replace(/_/g,' ')}</span>
          {ev.status_before && <span className="text-muted-foreground"> {ev.status_before} → {ev.status_after || ev.event_status_after}</span>}
          {ev.event_status_before && !ev.status_before && <span className="text-muted-foreground"> {ev.event_status_before} → {ev.event_status_after}</span>}
          {(ev.action_rationale || ev.reason || ev.correction_note) && <p className="text-muted-foreground mt-0.5">{ev.action_rationale || ev.reason || ev.correction_note}</p>}
          <p className="text-muted-foreground">{(ev.occurred_at||'').slice(0,16).replace('T',' ')}</p>
        </div>
      </div>
    ))}</div>
  );
}

// ═══════════════════════════════════════
// MORNING BRIEFING TAB (Phase 3.5)
// ═══════════════════════════════════════

function _typeLabel(type) {
  const labels = {
    not_submitted: 'Not Submitted', submitted_no_response: 'No Response',
    accepted_not_paid: 'Unpaid', denied_open: 'Denial Open',
    rejected_open: 'Rejection Open', remittance_unmatched: 'Unmatched',
    posting_exception: 'Posting Exception', rebill_ready: 'Rebill Ready',
    appeal_ready: 'Appeal Ready', denial_action_needed: 'Denial Action',
    underpayment_candidate: 'Underpayment',
  };
  return labels[type] || type.replace(/_/g, ' ');
}

function _fmtK(c) {
  if (c == null) return '$0';
  const d = Math.abs(c) / 100;
  return d >= 1000 ? `$${(d / 1000).toFixed(1)}k` : fmt(c);
}

function EnvironmentBadge({ env }) {
  const styles = {
    demo: 'bg-gray-100 text-gray-600 border-gray-200',
    sandbox: 'bg-amber-50 text-amber-700 border-amber-200',
    production: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium border rounded px-1.5 py-0.5 ${styles[env] || styles.demo}`} data-testid="environment-badge">
      <Globe className="h-2.5 w-2.5" />{env || 'demo'}
    </span>
  );
}

// ═══════════════════════════════════════
// TRANSPORT STATUS CHIP
// ═══════════════════════════════════════

function TransportStatusChip({ status }) {
  const colors = {
    queued: 'bg-gray-100 text-gray-600', sending: 'bg-blue-100 text-blue-700',
    sent: 'bg-emerald-100 text-emerald-700', failed: 'bg-red-100 text-red-700',
    timed_out: 'bg-orange-100 text-orange-700', vendor_received: 'bg-indigo-100 text-indigo-700',
  };
  return <span className={`inline-flex items-center text-[10px] font-medium rounded px-1.5 py-0.5 ${colors[status] || 'bg-muted text-muted-foreground'}`}>{(status || '').replace(/_/g, ' ')}</span>;
}

// ═══════════════════════════════════════
// SUBMISSION TAB (Phase 6 Enhanced)
// ═══════════════════════════════════════

function ExternalSourceBadge({ source }) {
  if (!source || source === 'demo') return null;
  if (source === 'external') {
    return <span className="inline-flex items-center gap-0.5 text-[9px] font-medium bg-indigo-100 text-indigo-700 border border-indigo-200 rounded px-1 py-0" data-testid="source-external-badge"><Globe className="h-2 w-2" />External</span>;
  }
  return <span className="inline-flex items-center text-[9px] font-medium bg-gray-100 text-gray-600 rounded px-1 py-0">{source}</span>;
}

function RawResponseViewer({ eventId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (data) { setOpen(!open); return; }
    try {
      const r = await api.get(`/rcm/external-responses/${eventId}`);
      setData(r.data);
      setOpen(true);
    } catch { setData(null); }
  };

  return (
    <div data-testid="raw-response-viewer">
      <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1.5" onClick={load}>
        <FileText className="h-2.5 w-2.5 mr-0.5" />{open ? 'Hide' : 'View'} Raw Response
      </Button>
      {open && data && (
        <div className="mt-1.5 bg-gray-900 text-gray-100 rounded p-2 text-[10px] font-mono overflow-auto max-h-[200px]">
          <pre>{JSON.stringify(data.raw_message_ref, null, 2)}</pre>
          {data.mapping_log?.length > 0 && (
            <div className="mt-2 border-t border-gray-700 pt-2">
              <span className="text-cyan-400">// Mapping audit</span>
              {data.mapping_log.map((m, i) => (
                <div key={i} className="text-gray-300">
                  {m.matching_status} — confidence: {m.confidence}% — {m.mapping_basis}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DenialStateBadge({ state }) {
  const styles = {
    open: 'bg-gray-100 text-gray-600 border-gray-200',
    under_review: 'bg-blue-100 text-blue-700 border-blue-200',
    awaiting_provider: 'bg-amber-100 text-amber-700 border-amber-200',
    awaiting_docs: 'bg-amber-100 text-amber-700 border-amber-200',
    awaiting_payer: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    corrected_ready: 'bg-teal-100 text-teal-700 border-teal-200',
    appeal_ready: 'bg-violet-100 text-violet-700 border-violet-200',
    written_off: 'bg-red-100 text-red-700 border-red-200',
    resolved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  };
  return <span className={`inline-flex items-center text-[9px] font-medium border rounded px-1.5 py-0.5 ${styles[state] || styles.open}`} data-testid="denial-state-badge">{(state || 'open').replace(/_/g, ' ')}</span>;
}

// ═══════════════════════════════════════
// DENIALS TAB (Group 2 — distinct from Rejections)
// ═══════════════════════════════════════

function _buildFocusSummary(overview, followUp, denials, underpay) {
  const lines = [];
  const fuSummary = followUp?.summary || {};
  const denCount = (denials?.queue || []).length;
  const upCount = underpay?.summary?.possible_underpayment || 0;

  const fuTypes = Object.entries(fuSummary).filter(([_, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  if (fuTypes.length > 0) {
    const [topType, topCount] = fuTypes[0];
    lines.push({ text: `${topCount} ${_typeLabel(topType).toLowerCase()} items need attention — start here.`, route: 'followup', filters: { type: topType } });
  }

  if (denCount > 0) {
    const totalDen = (denials?.queue || []).reduce((s, d) => s + (d.denial_amount || 0), 0);
    lines.push({ text: `${denCount} open denials totaling ${fmt(totalDen)} — review and choose action paths.`, route: 'denials', filters: { status: 'open' } });
  }

  if (upCount > 0) {
    lines.push({ text: `${upCount} underpayment candidates detected — review for possible rebill or appeal.`, route: 'posting', filters: { type: 'underpayment' } });
  }

  const ar = overview?.totals?.total_ar || 0;
  if (ar > 0) {
    lines.push({ text: `Total A/R is ${_fmtK(ar)} — check the A/R Aging tab in analytics for payer breakdown.`, route: 'remittance', filters: {} });
  }

  if (lines.length === 0) {
    lines.push({ text: 'No urgent items detected. All queues clear.', route: 'briefing', filters: {} });
  }

  return lines.slice(0, 4);
}

// ═══════════════════════════════════════
// ENVIRONMENT BADGE
// ═══════════════════════════════════════


export { PredictiveLabel, RiskBadge, ConfidenceText, FeedbackButtons, StatusChip, SlidePanel, Timeline, EnvironmentBadge, TransportStatusChip, ExternalSourceBadge, RawResponseViewer, DenialStateBadge, fmt };
export { _typeLabel, _fmtK, _buildFocusSummary };
